import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { moveOrderAfterWzRelease } from '../modules/orders/process/orderStatusAfterWzRelease';
import { shouldPreventEnterFormSubmit } from '../utils/formEnterGuard';

import { OrderDto } from '../dto/order';
import {
  buildManualWZCreatePayload,
  createManualWZ,
  fetchWZCreateOrder,
  fetchWZCreatePrefill,
  hasNonstandardWZOrderStatus,
  generateFullyCoveredWZ,
  mapWZPrefillItem,
  type WZCreateHandoverValues,
  type WZCreateItemDraft,
} from '../modules/wz/api/wzCreateApi';
import { getWZErrorMessage, isBlockingWZError } from '../modules/warehouse/wzErrorMessages';
import { askWarehouseSaveIntent } from '../modules/warehouse/warehouseSaveIntent';
import { isFutureReleaseDay, releaseWZDocument } from '../modules/warehouse/wzReleaseFlow';
import { useFeedback } from '../utils/feedback';
import { formatProductDimensions } from '../utils/productDimensions';
import { WZ_QUANTITY_MAX } from '../utils/warehouseQuantity';
import {
  isWZOrderFullyCoveredError,
  showWZFullyCoveredConfirm,
} from '../utils/wzOrderFullyCoveredMessage';
import { WZItemCard, WZItemField, WZItemsList } from './WZItemsView';
import WarehouseQuantityField from '../modules/warehouse/WarehouseQuantityField';
import {
  WAREHOUSE_CONVERTED_QUANTITY_MAX,
  extractWarehouseQuantitySetPatch,
  type WarehouseQuantityPatch,
} from '../modules/warehouse/warehouseQuantityConversion';
import {
  convertWZItemsToTarget,
  type WZBatchConversionTarget,
} from '../modules/wz/wzBatchQuantityConversion';
import WZBatchConversionMenu from '../modules/wz/components/WZBatchConversionMenu';
import {
  buildCustomerCardEditPath,
  resolveWZCreateDeliveryDefaults,
} from '../modules/wz/wzDeliveryAddress';
import { useBackNavigation } from '../hooks/useBackNavigation';
import './WZItemsView.css';
import './WZNew.css';

const { TextArea } = Input;
const { Title, Text } = Typography;

interface WZCreateFormValues extends Omit<WZCreateHandoverValues, 'issueDate' | 'receivedDate'> {
  orderId: string;
  issueDate: Dayjs;
  receivedDate?: Dayjs | null;
}

const formatWZItemDimensions = (item: WZCreateItemDraft) => formatProductDimensions({
  width: item.width,
  height: item.height,
  length: item.length,
  thickness: item.thickness,
  productCategoryDimensionDisplayProfile: 'GENERIC',
}) || '—';

const WZNew: React.FC = () => {
  const { t, i18n } = useTranslation(['wz', 'common', 'orders', 'transport']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const navigate = useNavigate();
  const goBack = useBackNavigation();
  const [searchParams] = useSearchParams();
  const initialOrderId = searchParams.get('orderId');
  // The order card links here with ?returnTo=/app/orders/<id>: Back and Cancel go
  // back to that order, not to the WZ list. Only in-app paths are accepted.
  const returnToParam = searchParams.get('returnTo');
  const leaveTo = returnToParam && /^\/app\/[^/]/.test(returnToParam) ? returnToParam : '/app/wz';
  // A WZ is issued only from the customer order (decision 2026-10-14): the
  // order card links here with ?orderId=, the order is fixed, and an entry
  // without it goes back to the list.
  const [form] = Form.useForm();

  const [selectedOrder, setSelectedOrder] = useState<OrderDto | null>(null);
  const [items, setItems] = useState<WZCreateItemDraft[]>([]);
  const [isFullyCoveredOrder, setIsFullyCoveredOrder] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deliveryDifferent, setDeliveryDifferent] = useState(false);
  const orderRequestRef = useRef(0);
  const prefillRequestRef = useRef(0);
  const customerAddressMissing = selectedOrder
    ? resolveWZCreateDeliveryDefaults(selectedOrder).customerAddressMissing
    : false;

  const applyOrder = useCallback(async (order: OrderDto) => {
    const prefillRequestId = ++prefillRequestRef.current;
    setSelectedOrder(order);
    setItems([]);
    setIsFullyCoveredOrder(false);
    setItemsLoading(true);
    const deliveryDefaults = resolveWZCreateDeliveryDefaults(order);
    setDeliveryDifferent(deliveryDefaults.showDeliveryInput);
    form.setFieldValue('deliveryAddress', deliveryDefaults.deliveryAddress);

    try {
      const prefill = await fetchWZCreatePrefill(order.id);
      if (prefillRequestRef.current !== prefillRequestId) return;

      const nextItems = prefill.items.map(mapWZPrefillItem);
      setItems(nextItems);
      const fullyCovered = nextItems.length === 0 && prefill.orderHasProducts;
      setIsFullyCoveredOrder(fullyCovered);
      if (fullyCovered) {
        feedback.info(t('wz:pickOrder.fullyCoveredHint'));
      }
    } catch (error) {
      if (prefillRequestRef.current !== prefillRequestId) return;
      console.error('Error loading WZ items from order:', error);
      setItems([]);
      setIsFullyCoveredOrder(false);
      feedback.error(t('wz:errors.loadOrder'));
    } finally {
      if (prefillRequestRef.current === prefillRequestId) setItemsLoading(false);
    }
  }, [form, feedback, t]);

  const loadOrder = useCallback(async (orderId: string) => {
    const orderRequestId = ++orderRequestRef.current;
    ++prefillRequestRef.current;
    setSelectedOrder(null);
    setItems([]);
    setIsFullyCoveredOrder(false);
    setItemsLoading(false);
    setOrderLoading(true);

    try {
      const order = await fetchWZCreateOrder(orderId);
      if (orderRequestRef.current !== orderRequestId) return;
      form.setFieldValue('orderId', order.id);
      await applyOrder(order);
    } catch (error) {
      if (orderRequestRef.current !== orderRequestId) return;
      console.error('Error loading order for WZ:', error);
      form.setFieldValue('orderId', undefined);
      feedback.error(t('wz:errors.loadOrder'));
    } finally {
      if (orderRequestRef.current === orderRequestId) setOrderLoading(false);
    }
  }, [applyOrder, form, feedback, t]);

  // After the operator completed the customer card in another tab: reload the
  // order's customer without discarding the lines already on this form. An
  // address typed here stays as the delivery address.
  const refreshCustomerCard = useCallback(async () => {
    if (!selectedOrder) return;
    try {
      const refreshed = await fetchWZCreateOrder(selectedOrder.id);
      const defaults = resolveWZCreateDeliveryDefaults(refreshed);
      const typedDeliveryAddress = String(form.getFieldValue('deliveryAddress') || '').trim();
      setSelectedOrder(refreshed);
      if (!typedDeliveryAddress) {
        setDeliveryDifferent(defaults.showDeliveryInput);
        form.setFieldValue('deliveryAddress', defaults.deliveryAddress);
      } else {
        setDeliveryDifferent(typedDeliveryAddress !== defaults.customerAddress);
      }
      feedback.success(t('wz:delivery.customerRefreshed'));
    } catch (error) {
      console.error('Error reloading customer for WZ:', error);
      feedback.error(t('wz:errors.loadOrder'));
    }
  }, [selectedOrder, form, feedback, t]);

  useEffect(() => {
    if (initialOrderId) void loadOrder(initialOrderId);
  }, [initialOrderId, loadOrder, form]);

  const selectedOrderLabel = selectedOrder
    ? [
      selectedOrder.displayOrderNumber || selectedOrder.orderNumber,
      selectedOrder.customerName || selectedOrder.customer?.name,
    ].filter(Boolean).join(' — ')
    : '';
  const hasPositiveItems = items.some((item) => item.productName.trim() !== '' && Number(item.quantity) > 0);

  const handleQuantityChange = (index: number, patch: WarehouseQuantityPatch) => {
    setItems((current) => current.map((item, itemIndex) => (
      itemIndex === index
        ? {
          ...item,
          ...extractWarehouseQuantitySetPatch(patch),
          quantity: patch.quantity,
          conversionUnit: patch.conversionUnit,
          conversionQuantity: patch.conversionQuantity,
          saleQuantity: patch.pricingQuantity ?? null,
          saleUnit: patch.pricingUnit ?? null,
          ...(patch.unit ? { unit: patch.unit } : {}),
        }
        : item
    )));
  };

  const removeItem = (index: number) => {
    setItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleBatchConversion = (target: WZBatchConversionTarget) => {
    const result = convertWZItemsToTarget(items, target);
    setItems(result.items);

    if (result.convertedCount === 0) {
      feedback.warning(t('wz:createForm.batchConversionNone'));
      return;
    }
    if (result.skippedCount > 0) {
      feedback.warning(t('wz:createForm.batchConversionPartial', {
        converted: result.convertedCount,
        skipped: result.skippedCount,
      }));
      return;
    }
    feedback.success(t('wz:createForm.batchConversionSuccess', { count: result.convertedCount }));
  };

  const handleSubmit = async (values: WZCreateFormValues) => {
    if (!selectedOrder || selectedOrder.id !== values.orderId) {
      feedback.error(t('wz:validation.orderRequired'));
      return;
    }

    const validItems = items.filter((item) => (
      item.productName.trim() !== '' && Number(item.quantity) > 0
    ));
    if (validItems.length === 0 && !isFullyCoveredOrder) {
      feedback.error(t('wz:errors.noItems'));
      return;
    }

    // One decision, one dialog: create the document AND release the goods, or
    // park it as a draft. See modules/warehouse/warehouseSaveIntent.
    const saveIntent = await askWarehouseSaveIntent({ documentType: 'WZ', modal, t });
    if (!saveIntent) {
      return;
    }

    const issueDate = values.issueDate.format('YYYY-MM-DD');
    // Optional (owner decision 2026-10-22, PW.4): empty until the goods are received.
    const receivedDate = values.receivedDate ? values.receivedDate.format('YYYY-MM-DD') : undefined;
    const handover: WZCreateHandoverValues = {
      ...values,
      issueDate,
      receivedDate,
      deliveryAddress: deliveryDifferent
        ? values.deliveryAddress
        : resolveWZCreateDeliveryDefaults(selectedOrder).customerAddress,
    };
    setSubmitting(true);
    try {
      let result;
      if (isFullyCoveredOrder) {
        try {
          result = await generateFullyCoveredWZ(selectedOrder.id, handover);
        } catch (error) {
          if (!isWZOrderFullyCoveredError(error)) throw error;
          const confirmed = await showWZFullyCoveredConfirm({ error, modal, t });
          if (!confirmed) return;
          result = await generateFullyCoveredWZ(selectedOrder.id, handover, true);
        }
      } else {
        const payload = buildManualWZCreatePayload({
          order: selectedOrder,
          items: validItems,
          handover,
          issueDate,
          releaseDate: issueDate,
          receivedDate,
        });
        result = await createManualWZ(payload, { modal, t });
        if (!result) return;
      }
      // Same key as WZDetail's create path: one document, one sentence.
      feedback.success(result?.wzNumber
        ? t('wz:success.created', { wzNumber: result.wzNumber })
        : t('wz:success.createdFallback'));
      const createdId = result?.id;
      if (saveIntent === 'POST' && createdId) {
        // A failed release leaves the document created as a draft and says why
        // — the operator lands on it and retries from "Rozlicz wydanie".
        const released = await releaseWZDocument({
          wzId: createdId,
          wzNumber: result?.wzNumber,
          modal,
          t,
          language: i18n.language,
          feedback,
          // The form date is the release date given at release (2026-10-14).
          releaseDate: issueDate,
        });
        // Same rule as „Wystaw WZ” on the ZK card (FF.10): a ZK before delivery → „W dostawie”.
        if (released && selectedOrder?.id) {
          const moved = await moveOrderAfterWzRelease(selectedOrder.id, t('wz:orderToDeliveryNote', { number: result?.wzNumber || '' }));
          if (moved === 'refused') feedback.warning(t('wz:orderToDeliveryRefused', { wzNumber: result?.wzNumber || '' }));
        }
      }
      navigate(createdId ? `/app/wz/${createdId}` : '/app/wz', { replace: true });
    } catch (error) {
      console.error('Error creating WZ document:', error);
      feedback.error(getWZErrorMessage(error, t, 'wz:errors.createFailed'), {
        sticky: isBlockingWZError(error),
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!initialOrderId) return <Navigate to="/app/wz" replace />;

  return (
    <div className="wz-create-page">
      <div className="wz-create-page__header">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => goBack(leaveTo)}
          aria-label={t('common:back')}
        />
        <div>
          <Title level={2} className="wz-create-page__title">{t('wz:newDocument')}</Title>
          <Text type="secondary">{t('wz:createForm.description')}</Text>
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onFinishFailed={() => feedback.warning(t('wz:fillRequired'))}
        // Live QA (agent INDIA): Enter in a single-line field — the sales
        // invoice number, in the reported case — implicit-submitted this form
        // and ISSUED the WZ mid-configuration. Same guard as the transport
        // form; textareas and the real submit button keep Enter.
        onKeyDown={(event) => {
          if (shouldPreventEnterFormSubmit(event)) {
            event.preventDefault();
          }
        }}
        scrollToFirstError
        initialValues={{
          issuedBy: '',
          receivedBy: '',
          issueDate: dayjs(),
        }}
      >
        <Space direction="vertical" size={20} className="wz-create-page__content">
          <Card title={t('wz:createForm.orderSection')}>
            <Form.Item
              name="orderId"
              label={t('wz:fields.order')}
              rules={[{ required: true, message: t('wz:validation.orderRequired') }]}
              className="wz-create-page__order-field"
            >
              <Select
                loading={orderLoading}
                disabled
                placeholder={t('wz:placeholders.selectOrder')}
                options={selectedOrder ? [{ value: selectedOrder.id, label: selectedOrderLabel }] : []}
              />
            </Form.Item>

            {selectedOrder && (
              <>
                {hasNonstandardWZOrderStatus(selectedOrder) && (
                  <Alert
                    type="warning"
                    showIcon
                    message={t('wz:pickOrder.nonstandardStatusTitle', {
                      status: t(`orders:statusOptions.${selectedOrder.status}`, {
                        defaultValue: t('common:unknownStatus'),
                      }),
                    })}
                    description={t('wz:pickOrder.nonstandardStatusDescription')}
                    className="wz-create-page__status-warning"
                  />
                )}
                <Descriptions
                  size="small"
                  column={1}
                  className="wz-create-page__order-summary"
                >
                  <Descriptions.Item label={t('wz:fields.orderNumber')}>
                    {selectedOrder.displayOrderNumber || selectedOrder.orderNumber}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('wz:fields.recipient')}>
                    {selectedOrder.customerName || selectedOrder.customer?.name || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('wz:fields.recipientAddress')}>
                    {selectedOrder.customer?.address || '—'}
                  </Descriptions.Item>
                  {selectedOrder.customer?.taxId && (
                    <Descriptions.Item label={t('wz:fields.recipientNIP')}>
                      {selectedOrder.customer.taxId}
                    </Descriptions.Item>
                  )}
                </Descriptions>
                <Text type="secondary" className="wz-create-page__recipient-source">
                  {t('wz:delivery.fromCustomerCard')}
                </Text>
                {customerAddressMissing && (
                  <Alert
                    type="warning"
                    showIcon
                    className="wz-create-page__status-warning"
                    data-testid="wz-missing-customer-address"
                    message={t('wz:delivery.missingCustomerAddressTitle')}
                    description={(
                      <Space direction="vertical" size={8}>
                        <span>{t('wz:delivery.missingCustomerAddressDescription')}</span>
                        <Space wrap>
                          {selectedOrder.customerId && (
                            <Link
                              to={buildCustomerCardEditPath(selectedOrder.customerId)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {t('wz:delivery.openCustomerCard')}
                            </Link>
                          )}
                          <Button size="small" onClick={() => { void refreshCustomerCard(); }}>
                            {t('wz:delivery.refreshCustomer')}
                          </Button>
                        </Space>
                      </Space>
                    )}
                  />
                )}
                <div className="wz-create-page__delivery-address">
                  {!customerAddressMissing && (
                    <Checkbox
                      checked={deliveryDifferent}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        const defaults = resolveWZCreateDeliveryDefaults(selectedOrder);
                        setDeliveryDifferent(checked);
                        form.setFieldValue(
                          'deliveryAddress',
                          checked
                            ? (selectedOrder.deliveryLocation || '').trim()
                            : defaults.customerAddress,
                        );
                      }}
                    >
                      {t('wz:delivery.differentFromRegistered')}
                    </Checkbox>
                  )}
                  {(deliveryDifferent || customerAddressMissing) && (
                    <Form.Item
                      name="deliveryAddress"
                      label={t('wz:fields.deliveryAddress')}
                      rules={[{
                        required: true,
                        whitespace: true,
                        message: customerAddressMissing
                          ? t('wz:validation.deliveryAddressRequiredNoCustomerAddress')
                          : t('wz:validation.deliveryAddressRequired'),
                      }]}
                    >
                      <TextArea rows={2} placeholder={t('wz:placeholders.deliveryAddress')} maxLength={500} />
                    </Form.Item>
                  )}
                </div>
              </>
            )}
          </Card>

          <Card title={t('wz:createForm.datesSection')}>
            <Row gutter={[16, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="issueDate"
                  label={t('wz:fields.issueDate')}
                  rules={[{ required: true, message: t('wz:validation.issueDateRequired') }]}
                  // The same date is the release date of „Zapisz i wydaj” (2026-10-14).
                  extra={t('wz:confirm.releaseDateHelp')}
                >
                  <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" disabledDate={isFutureReleaseDay} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="receivedDate"
                  label={t('wz:fields.deliveryDate')}
                  extra={t('wz:fields.deliveryDateHelp')}
                >
                  <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            title={t('wz:createForm.itemsSection')}
            className="wz-items-section"
            extra={items.length > 1 ? (
              <WZBatchConversionMenu onConvert={handleBatchConversion} />
            ) : null}
          >
            <Spin spinning={itemsLoading}>
              {!selectedOrder || items.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={selectedOrder
                    ? t(isFullyCoveredOrder ? 'wz:createForm.fullyCoveredAction' : 'wz:createForm.noItems')
                    : t('wz:createForm.selectOrderFirst')}
                />
              ) : (
                <WZItemsList>
                  {items.map((item, index) => (
                    <WZItemCard
                      key={`${item.productVariantId || item.productName}-${index}`}
                      index={index}
                      positionLabel={t('wz:itemFields.position', { number: index + 1 })}
                      productLabel={t('wz:fields.productName')}
                      product={item.productName}
                      action={(
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => removeItem(index)}
                          aria-label={t('wz:createForm.omitItem')}
                        >
                          {t('wz:createForm.omitItem')}
                        </Button>
                      )}
                    >
                      <WZItemField label={t('wz:fields.dimensions')} wide>
                        {formatWZItemDimensions(item)}
                      </WZItemField>
                      <WZItemField label={t('wz:fields.quantity')} emphasis>
                        <WarehouseQuantityField
                          line={item}
                          pricingQuantity={item.saleQuantity}
                          pricingUnit={item.saleUnit}
                          onChange={(patch) => handleQuantityChange(index, patch)}
                          maxQuantity={WZ_QUANTITY_MAX}
                          maxConvertedQuantity={WAREHOUSE_CONVERTED_QUANTITY_MAX}
                          noDimensionsLabel={t('wz:conversion.noDimensions')}
                          unitLabel={t('wz:fields.unit')}
                          onZero={() => removeItem(index)}
                        />
                      </WZItemField>
                      <WZItemField label={t('wz:source.fieldLabel')}>
                        {t('wz:source.summary.fifo')}
                      </WZItemField>
                    </WZItemCard>
                  ))}
                </WZItemsList>
              )}
            </Spin>
          </Card>

          <Card title={t('wz:createForm.releaseSection')}>
            <Alert
              type="info"
              showIcon
              message={t('wz:createForm.releaseHint')}
              className="wz-create-page__release-hint"
            />
            <Row gutter={[16, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="issuedBy"
                  label={t('wz:fields.issuedBy')}
                  rules={[
                    { required: true, message: t('wz:validation.issuedByRequired') },
                    { max: 100, message: t('wz:validation.personNameMax') },
                  ]}
                >
                  <Input placeholder={t('wz:placeholders.issuedBy')} maxLength={100} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="vehicleRegistration"
                  label={t('wz:fields.vehicleRegistration')}
                  // WP14: self-pickup — the customer's own vehicle is recorded
                  // on the WZ (no transport, no transport cost).
                  extra={selectedOrder?.deliveryMode === 'CUSTOMER_PICKUP'
                    ? t('wz:customerPickup.vehicleHint')
                    : undefined}
                >
                  <Input placeholder={t('wz:placeholders.vehicleRegistration')} maxLength={100} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="receivedBy"
                  label={t('wz:fields.receivedBy')}
                  rules={[{ max: 100, message: t('wz:validation.personNameMax') }]}
                >
                  <Input placeholder={t('wz:placeholders.receivedBy')} maxLength={100} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="salesInvoiceNumber" label={t('wz:fields.salesInvoiceNumber')}>
                  <Input placeholder={t('wz:placeholders.salesInvoiceNumber')} maxLength={100} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item name="notes" label={t('wz:fields.notes')}>
                  <TextArea rows={4} placeholder={t('wz:placeholders.notes')} maxLength={1000} showCount />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <div className="wz-create-page__actions">
            <Button onClick={() => navigate(leaveTo)}>{t('common:cancel')}</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              disabled={!selectedOrder || itemsLoading || orderLoading || (!hasPositiveItems && !isFullyCoveredOrder)}
            >
              {t('wz:actions.create')}
            </Button>
          </div>

          {selectedOrderLabel && (
            <Text type="secondary" className="wz-create-page__selection-note">
              {t('wz:createForm.selectedOrder', { order: selectedOrderLabel })}
            </Text>
          )}
        </Space>
      </Form>
    </div>
  );
};

export default WZNew;
